
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const useUserDevices = (includeAllDevicesForSuperuser = false) => {
  return useQuery({
    queryKey: ["user-devices", includeAllDevicesForSuperuser],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("No authenticated user");
      }

      // Check user role if needed
      let userRole = null;
      if (includeAllDevicesForSuperuser) {
        const { data: role } = await supabase.rpc('get_user_role', {
          user_id: user.id
        });
        userRole = role;
      }

      // For superusers, return all devices; for internal_staff/advisor, return all-then-filter to demo;
      // for others, return assigned devices
      const isDemoOnly = userRole === 'internal_staff' || userRole === 'advisor';
      if (includeAllDevicesForSuperuser && (userRole === 'superuser' || isDemoOnly)) {
        // Get all devices for superusers
        const { data: devices, error: devicesError } = await supabase
          .from('device_details')
          .select('device_id, device_name, device_status')
          .order('device_last_updated', { ascending: false, nullsFirst: false });

        if (devicesError) {
          console.error('Error fetching all devices for superuser:', devicesError);
          throw devicesError;
        }

        // Demo-only roles see ONLY demo devices (protects customer cameras)
        const filtered = isDemoOnly
          ? (devices || []).filter(d => (d as any).device_status?.toLowerCase() === 'demo')
          : (devices || []);

        // Log superuser access for audit
        console.log(`User ${user.email} (${userRole}) accessed ${filtered.length} devices`);

        return filtered;
      } else {
        // Standard user behavior - get only assigned devices
        const { data: assignments, error: assignmentsError } = await supabase
          .from('device_assignments')
          .select('device_id')
          .eq('user_id', user.id);

        if (assignmentsError) {
          console.error('Error fetching device assignments:', assignmentsError);
          throw assignmentsError;
        }

        if (!assignments || assignments.length === 0) {
          return [];
        }

        // Get device details for assigned devices
        const deviceIds = assignments.map(a => a.device_id);
        const { data: devices, error: devicesError } = await supabase
          .from('device_details')
          .select('device_id, device_name')
          .in('device_id', deviceIds);

        if (devicesError) {
          console.error('Error fetching device details:', devicesError);
          throw devicesError;
        }

        return devices || [];
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
